export const WORKER_HEADER_HEIGHT = 64;
export const DAY_HEADER_HEIGHT = 50;
export const TIME_AXIS_END_GUTTER = 50;
/** Margine verticale per non tagliare primo/ultimo orario sotto lo sticky header. */
export const TIME_LABEL_EDGE_INSET = 8;
export const SLOT_MINUTES = 5;
export const MINOR_TICK_MINUTES = 15;
export const MAJOR_TICK_MINUTES = 30;

export const DESKTOP_CARD_SPACE = 160;
export const MOBILE_CARD_SPACE = 120;
export const MIN_MOBILE_CHAIR_WIDTH = 100;
export const TIME_AXIS_WIDTH = 50;
export const TIME_AXIS_WIDTH_MOBILE = 44;
export const DESKTOP_STEP_SIZE = 14;
/** Slot più compatti su mobile (px per 5 minuti), usato solo se non c’è un zoom salvato. */
export const MOBILE_STEP_SIZE = 8;
export const MIN_STEP_SIZE = 7;
export const MAX_STEP_SIZE = 24;
/**
 * Cap tipografia/header su mobile: altrimenti uno stepSize legacy (14–24)
 * fa esplodere i font (14/8=1.75) su Fold / telefoni stretti.
 */
export const MAX_MOBILE_ZOOM_SCALE = 1.15;
export const CALENDAR_PARAMS_STORAGE_KEY = 'calendarParams';

export const clampStepSize = (value, fallback = DESKTOP_STEP_SIZE) => {
  const size = Number(value);
  if (!Number.isFinite(size)) return fallback;
  return Math.min(MAX_STEP_SIZE, Math.max(MIN_STEP_SIZE, Math.round(size)));
};

export const getInitialStepSize = (savedStepSize, isMobile) => {
  const fallback = isMobile ? MOBILE_STEP_SIZE : DESKTOP_STEP_SIZE;
  if (savedStepSize == null || savedStepSize === '') return fallback;
  const clamped = clampStepSize(savedStepSize, fallback);
  // Prima dello zoom mobile molti device hanno salvato il default desktop (14):
  // su telefono/Fold diventava scala 1.75× e orari/card si sovrapponevano.
  if (isMobile && clamped === DESKTOP_STEP_SIZE) return MOBILE_STEP_SIZE;
  return clamped;
};

/**
 * Scala zoom UI rispetto al default del dispositivo (desktop 14 / mobile 8).
 * Compatto < 1, ampio > 1 — usata per larghezza poltrone, header e tipografia.
 * Su mobile la tipografia è soft-cappata (lo stepSize può restare più alto per le slot).
 */
export const getCalendarZoomScale = (stepSize, isMobile = false) => {
  const baseline = isMobile ? MOBILE_STEP_SIZE : DESKTOP_STEP_SIZE;
  const raw = clampStepSize(stepSize, baseline) / baseline;
  if (isMobile) return Math.min(MAX_MOBILE_ZOOM_SCALE, raw);
  return raw;
};

export const scaleLayoutPx = (basePx, zoomScale, { min = 1 } = {}) => {
  const scaled = Math.round(Number(basePx) * Number(zoomScale));
  if (!Number.isFinite(scaled)) return Math.max(min, Number(basePx) || min);
  return Math.max(min, scaled);
};

/** Larghezza colonna poltrona proporzionale allo zoom (desktop) o al fit viewport (mobile). */
export const getZoomedCardSpace = (stepSize, options = {}) => {
  const { isMobile = false, fitted = null, maxWidth = 280 } = options;
  const minWidth = options.minWidth ?? (isMobile ? MIN_MOBILE_CHAIR_WIDTH : 80);
  const zoomScale = getCalendarZoomScale(stepSize, isMobile);
  const base = isMobile ? MOBILE_CARD_SPACE : DESKTOP_CARD_SPACE;
  const fromZoom = scaleLayoutPx(base, zoomScale, { min: minWidth });
  // Se abbiamo già una larghezza che riempie lo schermo, non moltiplicare di nuovo per zoom
  // (altrimenti su Fold una sola poltrona diventa più larga del viewport).
  const width =
    fitted != null && Number.isFinite(fitted)
      ? Math.round(fitted)
      : fromZoom;
  return Math.min(maxWidth, Math.max(minWidth, width));
};

export const getDayHeaderOffset = (showHeader, zoomScale = 1) =>
  showHeader ? scaleLayoutPx(DAY_HEADER_HEIGHT, zoomScale) : 0;

export const getWorkerHeaderOffset = (showHeader, zoomScale = 1) =>
  getDayHeaderOffset(showHeader, zoomScale) + scaleLayoutPx(WORKER_HEADER_HEIGHT, zoomScale);

/** Stesso offset delle righe griglia: le label si centrano via CSS (translateY). */
export const getTimeLabelOffset = (showHeader, zoomScale = 1) =>
  getWorkerHeaderOffset(showHeader, zoomScale);

export const getSchedulerInnerHeight = (slotCount, stepSize, showHeader, zoomScale = 1) =>
  slotCount * stepSize + getWorkerHeaderOffset(showHeader, zoomScale) + TIME_AXIS_END_GUTTER;

export const timeToMinutes = ({ hours = 0, minutes = 0 } = {}) =>
  hours === 24 ? 24 * 60 : hours * 60 + minutes;

export const formatClockLabel = (totalMinutes) => {
  if (totalMinutes >= 24 * 60) return '24:00';
  const hours = Math.floor(totalMinutes / 60);
  const minutes = totalMinutes % 60;
  return `${hours}:${String(minutes).padStart(2, '0')}`;
};

export const getCalendarSlotCount = (startWorkTime, endWorkTime) =>
  Math.max(
    Math.round((timeToMinutes(endWorkTime) - timeToMinutes(startWorkTime)) / SLOT_MINUTES),
    0,
  );

export const getCalendarTicks = (
  startWorkTime,
  endWorkTime,
  intervalMinutes,
  { fromOffset = 0, includeEnd = false } = {},
) => {
  const start = timeToMinutes(startWorkTime);
  const end = timeToMinutes(endWorkTime);
  const ticks = [];

  for (let time = start + fromOffset; time < end; time += intervalMinutes) {
    ticks.push(time);
  }

  if (includeEnd && end > start) {
    ticks.push(end);
  }

  return ticks;
};

export const getTickOffset = (tickMinutes, startWorkTime, stepSize, headerOffset) =>
  ((tickMinutes - timeToMinutes(startWorkTime)) / SLOT_MINUTES) * stepSize + headerOffset;

/**
 * Offset label: allineato alla griglia, con inset su primo/ultimo tick
 * per non finire sotto lo sticky corner / fuori dal gutter.
 */
export const getTimeLabelTop = (
  tickMinutes,
  startWorkTime,
  stepSize,
  headerOffset,
  { isFirst = false, isLast = false } = {},
) => {
  const base = getTickOffset(tickMinutes, startWorkTime, stepSize, headerOffset);
  if (isFirst) return base + TIME_LABEL_EDGE_INSET;
  if (isLast) return base - TIME_LABEL_EDGE_INSET;
  return base;
};

export const shiftWorkTime = (workTime, deltaMinutes) => {
  const total = Math.min(Math.max(timeToMinutes(workTime) + deltaMinutes, 0), 24 * 60);
  return {
    hours: Math.floor(total / 60),
    minutes: total % 60,
  };
};

export const getVisibleEndWorkTime = (endWorkTime) =>
  shiftWorkTime(endWorkTime, -MAJOR_TICK_MINUTES);

/**
 * Y del puntatore in pixel di layout, anche con `html { zoom }` o visualViewport.
 * clientY/getBoundingClientRect sono nello spazio visivo; offsetHeight è CSS.
 */
export const getPointerLayoutOffsetY = (clientY, element) => {
  if (!element || typeof element.getBoundingClientRect !== 'function') return 0;
  const rect = element.getBoundingClientRect();
  const visualY = clientY - rect.top;
  const visualHeight = rect.height;
  const layoutHeight = element.offsetHeight;
  if (visualHeight > 0 && layoutHeight > 0 && Math.abs(visualHeight - layoutHeight) > 0.5) {
    return (visualY / visualHeight) * layoutHeight;
  }
  return visualY;
};

export const getSlotIndexFromPointer = (clientY, element, stepSize) => {
  const size = Number(stepSize);
  if (!Number.isFinite(size) || size <= 0) return 0;
  return Math.max(0, Math.floor(getPointerLayoutOffsetY(clientY, element) / size));
};

/** Larghezza poltrona: riempie lo schermo se stanno tutte, altrimenti min e scroll. */
export const getFittedCardSpace = (
  containerWidth,
  chairCount,
  {
    timeAxisWidth = TIME_AXIS_WIDTH_MOBILE,
    minWidth = MIN_MOBILE_CHAIR_WIDTH,
    fallback = MOBILE_CARD_SPACE,
  } = {},
) => {
  const count = Math.max(1, Number(chairCount) || 1);
  const available = Math.max(0, (Number(containerWidth) || 0) - (Number(timeAxisWidth) || 0));
  if (available <= 0) return fallback;
  const fitted = Math.floor(available / count);
  return fitted >= minWidth ? fitted : fallback;
};
